import { generateBooleanSchema, generateRootObjectSchema, generateStringSchema, generateUrlSchema, generateDateTimeSchema, generateArraySchema, generateDurationSchema, generateNumberSchema, generateObjectSchema, generateColorSchema } from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec, ImageParticleSpec, ButtonParticleSpec } from "components/cards/particles/YinzCam";

export const specification = new BaseAtomSpec("Regular Horizontal Card", generateRootObjectSchema({
    container: generateAtomContainerSchema(),
    overlayPadding: generateStringSchema("Overlay Padding", "The padding (inset) of the overlay text/buttons relative to the background image.", {
        defaultValue: '3rem'
    }),
    mediaType: generateStringSchema("Type", "The type of this media item. This controls the media-type indicator icon as well as the default media item deep link.", {
        choices: ['article', 'video', 'gallery', 'audio'],
        choiceTitles: ["News Article", "Video", "Photo Gallery", "Audio"]
    }),
    slug: generateStringSchema("Slug", "The slug for this media item. This will be used to generate the media item link if an explicit link is not provided."),
    link: generateUrlSchema("Link", "The link for this media item. The default link will be generated from the slug if this is not provided (and the slug is)."),
    publishDateTime: generateDateTimeSchema("Publish Date and Time", "The date and time that this content was published."),
    viewTimeDuration: generateDurationSchema("View Time Duration", "The duration that an average user would take to completely consume this content."),
    views: generateNumberSchema("Views", "The number of views of this content."),
    thumbnail: ImageParticleSpec.generateSchema("Image Media", "Upload Image file", {
        defaultValues: { sizing: "cover", fitVerticalAlign: 'top', role: 'general' }
    }),
    titleText: TextParticleSpec.generateSchema("Title Text", "Enter title text", {
        defaultValues: { size: "2.5rem", weight: "900", lineClamp: 3 }
    }),
    descriptionText: TextParticleSpec.generateSchema("Description text", "Enter a description text", {
        defaultValues: { size: "1.25rem", weight: "400", lineClamp: 3 }
    }),
    imgClockIcon: ImageParticleSpec.generateSchema("Clock Icon", "Select/upload an icon for clock", {
        defaultValues: { container: { width: "1.25rem", height: "1.25rem" } }
    }),
    txtDateTime: TextParticleSpec.generateSchema("Date Time Text", "The text styling for the date/time text.", {
        defaultValues: { size: '1rem', weight: '700', transform: 'capitalize', lineClamp: 1, lineHeight: "normal" },
        uneditableFields: { text: true }
    }),
    imgShareIcon: ImageParticleSpec.generateSchema("Share Icon", "Select/upload an icon for share option", {
        defaultValues: { container: { width: "2rem", height: "2rem" } }
    }),
    shouldDisplayBodyText: generateBooleanSchema("Should display body text?", "Select if we should display body text", {
        defaultValue: true,
    }),
    reverseOrientation: generateStringSchema("Content Orientation", "Specify orientation of content", {
        choices: ["image-first", "text-first"],
        choiceTitles: ["Image First", "Text First"],
        defaultValue: "image-first"
    }),
    displayDateDuration: generateStringSchema("Display Date or Duration", "Select if to display date or duration", {
        choices: ["date", "duration"],
        choiceTitles: ["Date", "Duration"],
        defaultValue: "date",
    }),
    shouldDisplayTime: generateBooleanSchema("Should Display Time?", "Should Display Time?", {
        defaultValue: true
    }),
    shouldDisplayDuration: generateBooleanSchema("Should Display Duration?", "Should Display Duration?", {
        defaultValue: false
    }),
    shouldDisplayViews: generateBooleanSchema("Should Display Views?", "Should Display Views?", {
        defaultValue: false
    }),
    textBoxWidth: generateStringSchema("Text Box Width", "Text Box Width %"),
    categories: generateArraySchema("Categories", "The categories to list for the media item.",
        ButtonParticleSpec.generateSchema("Category", "A category for the media item.", {
            defaultValues: {
                showImage: false, container: { themeMode: 'inverted' }, text: { size: "1.25rem", weight: "700" }
            }
        })
    ),
    shouldDisplayCategories: generateBooleanSchema("Should display categories?", "Specify if categories should be displayed"),
    displayOnlyFirstCategory: generateBooleanSchema(
      'Should display only one category?',
      'Specify if only one should be displayed',
      { defaultValue: false }
    ),
    enableCategoryPill: generateBooleanSchema(
        "Enable Category Pill?",
        "Display media type text as a category badge in the category list area.",
        { defaultValue: false }
      ),
      categoryPillPadding: generateStringSchema("Category Pill Padding", "Padding around the category pill area."),
      categoryPill: ButtonParticleSpec.generateSchema(
        "Category Pill",
        "Styles for the media-type category badge displayed in the category list area.",
        {
          defaultValues: {
            showImage: false,
            showText: true,
            container: {
              themeMode: "inverted",
              background: { visible: true },
              border: { visible: true, radius: "8px" },
              padding: "0.35rem 0.6rem",
            },
            text: {
              size: "1rem",
              weight: "700",
            },
          },
        }
      ),
      enableCategoryPillLabelMapping: generateBooleanSchema(
        "Enable Category Label Mapping?",
        "Use content category names to map the category pill text (falls back to media type when no rule matches).",
        { defaultValue: false }
      ),
      categoryPillLabelMappings: generateArraySchema(
        "Category Label Mappings",
        "Rules to map content category name to a custom category pill label.",
        generateObjectSchema("Category Label Mapping", "Map category name to category pill label.", {
          mediaType: generateStringSchema(
            "Media Type",
            "Optional media type filter (e.g. video). Leave blank to match all media types."
          ),
          sourceCategoryName: generateStringSchema(
            "Source Category Name",
            "Category name from content data to match (case-insensitive)."
          ),
          mappedLabel: generateStringSchema("Mapped Label", "Badge label to display when the source category matches."),
          bgColor: generateColorSchema("Mapped BG Color", "Optional background color for this mapped category label."),
          textColor: generateColorSchema("Mapped Text Color", "Optional text color for this mapped category label."),
        })
      ),
      categoryPillMediaTypeOverrides: generateArraySchema(
        "Category Media Type Overrides",
        "Optional per-media-type overrides for category pill label and colors.",
        generateObjectSchema("Media Type Override", "Override category pill behavior for one media type.", {
          mediaType: generateStringSchema("Media Type", "Media type to override.", {
            choices: ["article", "video", "gallery", "audio"],
            choiceTitles: ["News Article", "Video", "Photo Gallery", "Audio"],
          }),
          label: generateStringSchema("Label Override", "Optional label override for this media type."),
          bgColor: generateColorSchema("BG Color Override", "Optional background color override for this media type."),
          textColor: generateColorSchema("Text Color Override", "Optional text color override for this media type."),
        })
      ),
    imgCameraIcon: ImageParticleSpec.generateSchema("Camera Icon", "Select/upload an icon for Camera", {
        defaultValues: { container: { width: "2rem", height: "2rem" }, icon: "yc_icon_camera" }
    }),
    totalPhotos: generateStringSchema("Total Photos in Album", "Total Photos in Album"),
    shouldDisplayPhotosCount: generateBooleanSchema("Should Display Photos Count?", "Should Display Photos Count?", {
        defaultValue: false,
    }),
    header: TextParticleSpec.generateSchema("Header Text", "The text styling for the header text.", {
        defaultValues: { size: '1rem', weight: '600', transform: 'capitalize' },
        uneditableFields: { text: true }
    }),
    photosCountPosition: generateStringSchema("Photos Count Pill Position", "Photos Count Pill Position", {
        choices: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        choiceTitles: ['Top Left', 'Top Right', 'Bottom Left', 'Bottom Right'],
        defaultValue: 'top-right'
    }),
    videoRuntime: generateStringSchema("Video Runtime", "The runtime/length of the video. Accepts seconds (e.g. \"332\") or an ISO 8601 duration (e.g. \"PT5M32S\")."),
    shouldDisplayVideoRuntime: generateBooleanSchema("Should Display Video Runtime?", "Display the video runtime badge over the thumbnail for video media items.", {
        defaultValue: false
    }),
    videoIsLive: generateBooleanSchema("Is Live?", "Show a \"Live\" label in the runtime badge instead of the runtime/length.", {
        defaultValue: false
    }),
    videoLiveLabel: generateStringSchema("Live Label", "The label to show in the badge when the video is live. Leave blank to use the default translated \"LIVE\" text.", {
        defaultValue: ''
    }),
    videoRuntimePosition: generateStringSchema("Video Runtime Position", "Position of the video runtime badge over the thumbnail.", {
        choices: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        choiceTitles: ['Top Left', 'Top Right', 'Bottom Left', 'Bottom Right'],
        defaultValue: 'top-left'
    }),
    videoRuntimeInset: generateStringSchema("Video Runtime Inset", "Distance from thumbnail edge for the runtime badge.", {
        defaultValue: '1rem'
    }),
    videoRuntimeBadge: ButtonParticleSpec.generateSchema("Video Runtime Badge", "Styles for the video runtime badge displayed over the thumbnail.", {
        defaultValues: {
            showImage: false,
            showText: true,
            container: {
                background: { visible: true, color: 'rgba(0, 0, 0, 0.36)' },
                border: { visible: true, style: 'solid', width: '2px', color: '#ffffff', radius: '0.5rem' },
                padding: '0.4rem 0.75rem'
            },
            text: {
                size: "0.9rem",
                weight: "700",
                color: '#ffffff'
            }
        }
    })
}));

